import { useState, useEffect, useCallback } from 'react';
import splitScreenManager, { SPLIT_MODES } from '../../../../utils/SplitScreenManager';

export const useSplitScreenManager = (refreshMapView, containerId, currentVehicle, currentDateRange) => {
  // Состояния
  const [expandedMode, setExpandedMode] = useState(false);
  const [showReportChooser, setShowReportChooser] = useState(false);
  const [containerToFill, setContainerToFill] = useState(null);
  const [splitDirection, setSplitDirection] = useState(null);

  // Переключение развернутого режима (точная копия из TrackMap)
  const toggleExpandedMode = useCallback(() => {
    console.log('TrackReport: Переключение развернутого режима');
    setExpandedMode(prev => !prev);
    
    // Обновляем карту после изменения размера
    setTimeout(() => {
      refreshMapView();
    }, 300);
  }, [refreshMapView]);

  // Изменение режима разделения экрана (исправленная логика без рекурсии)
  const changeSplitMode = useCallback((mode) => {
    try {
      console.log('TrackReport: Изменение режима разделения на:', mode);
      
      if (mode === 'single') {
        // Сбрасываем все разделения
        const success = splitScreenManager.changeSplitMode(SPLIT_MODES.SINGLE);
        
        if (success) {
          console.log('TrackReport: Режим разделения успешно изменен на single');
          
          // Создаем и отправляем событие о смене режима разделения
          const event = new CustomEvent('splitModeChanged', {
            detail: { mode }
          });
          document.dispatchEvent(event);
        } else {
          console.warn('TrackReport: Не удалось изменить режим разделения на single');
        }
        
        // Сохраняем в localStorage
        localStorage.setItem('splitScreenMode', mode);
        
        // Обновляем карту после изменения режима
        setTimeout(() => {
          refreshMapView();
        }, 300);
      } else {
        console.warn('TrackReport: changeSplitMode вызван для режима ' + mode + ', используйте handleHorizontalSplit или handleVerticalSplit');
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при изменении режима разделения:', error);
    }
  }, [refreshMapView]);

  // Обработчик горизонтального разделения (реализация как в TrackMap)
  const handleHorizontalSplit = useCallback(() => {
    console.log('TrackReport: Горизонтальное разделение экрана');
    
    // Находим контейнер перед разделением - сначала пытаемся найти через DOM
    let container = null;
    
    // Попытка 1: найти через ближайший split-screen-container
    const mapWrapper = document.querySelector('.tm-map-wrapper');
    if (mapWrapper) {
      container = mapWrapper.closest('.split-screen-container');
    }
    
    // Попытка 2: найти по containerId
    if (!container && containerId) {
      container = document.getElementById(containerId) || 
                 document.querySelector(`[data-container-id="${containerId}"]`);
    }
    
    // Попытка 3: найти любой активный контейнер
    if (!container) {
      container = document.querySelector('.split-screen-container[data-active="true"]') ||
                 document.querySelector('.split-screen-container');
    }
    
    if (!container) {
      console.error('TrackReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('TrackReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`TrackReport: Разделяем контейнер ${targetContainerId} горизонтально`);
    
    // Сохраняем направление разделения
    setSplitDirection('horizontal');
    
    // Создаем пользовательское событие для запроса на разделение
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'horizontal',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
  }, [setSplitDirection, containerId]);

  // Обработчик вертикального разделения (реализация как в TrackMap)
  const handleVerticalSplit = useCallback(() => {
    console.log('TrackReport: Вертикальное разделение экрана');
    
    // Находим контейнер перед разделением - сначала пытаемся найти через DOM
    let container = null;
    
    // Попытка 1: найти через ближайший split-screen-container
    const mapWrapper = document.querySelector('.tm-map-wrapper');
    if (mapWrapper) {
      container = mapWrapper.closest('.split-screen-container');
    }
    
    // Попытка 2: найти по containerId
    if (!container && containerId) {
      container = document.getElementById(containerId) || 
                 document.querySelector(`[data-container-id="${containerId}"]`);
    }
    
    // Попытка 3: найти любой активный контейнер
    if (!container) {
      container = document.querySelector('.split-screen-container[data-active="true"]') ||
                 document.querySelector('.split-screen-container');
    }
    
    if (!container) {
      console.error('TrackReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('TrackReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`TrackReport: Разделяем контейнер ${targetContainerId} вертикально`);
    
    // Сохраняем направление разделения
    setSplitDirection('vertical');
    
    // Создаем событие для запроса разделения по вертикали
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'vertical',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
  }, [setSplitDirection, containerId]);

  const handleSingleMode = useCallback(() => {
    console.log('TrackReport: Одиночный режим экрана');
    changeSplitMode('single');
  }, [changeSplitMode]);

  const handleGoBack = useCallback(() => {
    console.log('TrackReport: Возврат к предыдущему режиму');
    try {
      if (splitScreenManager && splitScreenManager.goBack) {
        splitScreenManager.goBack();
      } else {
        console.warn('TrackReport: splitScreenManager.goBack недоступен');
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при возврате к предыдущему режиму:', error);
    }
  }, []);

  // Обработчик изменения режима разделения экрана (копия из TrackMap)
  const handleSplitModeChanged = useCallback((event) => {
    try {
      console.log('TrackReport: Получено событие изменения режима разделения:', event.detail);
      
      const { direction } = event.detail;
      
      // Обновляем состояние направления разделения
      if (direction) {
        setSplitDirection(direction);
        console.log('TrackReport: Обновлено направление разделения:', direction);
      }
      
      // Обновляем карту после изменения
      setTimeout(() => {
        refreshMapView();
      }, 300);
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке изменения режима разделения:', error);
    }
  }, [refreshMapView]);

  // Обработчик создания панели (копия из TrackMap)
  const handlePanelCreated = useCallback((event) => {
    try {
      console.log('TrackReport: Получено событие создания панели:', event.detail);
      
      const { containerId, direction } = event.detail;
      
      if (containerId) {
        setContainerToFill(containerId);
        console.log('TrackReport: Установлен контейнер для заполнения:', containerId);
      }
      
      if (direction) {
        setSplitDirection(direction);
        console.log('TrackReport: Установлено направление разделения:', direction);
      }
      
      // Показываем ReportChooser
      setShowReportChooser(true);
      window.reportChooserModalOpen = true;
      
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке создания панели:', error);
    }
  }, []);

  // Обработчик запроса селектора отчетов (копия из TrackMap)
  const handleRequestReportSelector = useCallback((event) => {
    try {
      console.log('TrackReport: Получен запрос селектора отчетов:', event.detail);
      
      const { containerId, splitDirection: requestDirection } = event.detail;
      
      // Предотвращаем повторное открытие модального окна
      if (window.reportChooserModalOpen) {
        console.log('TrackReport: ReportChooser уже открыт, пропускаем');
        return;
      }
      
      // Устанавливаем контейнер для заполнения
      if (containerId) {
        setContainerToFill(containerId);
        console.log('TrackReport: Установлен контейнер для заполнения отчетом:', containerId);
      }
      
      // Устанавливаем направление разделения
      if (requestDirection) {
        setSplitDirection(requestDirection);
        console.log('TrackReport: Установлено направление разделения:', requestDirection);
      }
      
      // Показываем ReportChooser
      setTimeout(() => {
        setShowReportChooser(true);
        window.reportChooserModalOpen = true;
      }, 100);
      
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке запроса селектора отчетов:', error);
    }
  }, []);

  // Обработчик выбора отчета (обновленная версия для создания отчетов)
  const handleReportSelect = useCallback((selectedReportType) => {
    try {
      console.log('TrackReport: Выбран отчет:', selectedReportType);
      
      // Закрываем ReportChooser сразу
      setShowReportChooser(false);
      window.reportChooserModalOpen = false;
      
      if (containerToFill) {
        // Если у нас есть контейнер для заполнения из события разделения,
        // создаем отчет для этого контейнера
        console.log(`TrackReport: Создаем отчет типа ${selectedReportType} для контейнера ${containerToFill}`);
        
        // Дополнительный вывод для отладки информации о состоянии и контейнере
        console.log('TrackReport: Детали создания отчета:', { 
          containerToFill,
          vehicle: currentVehicle,
          startDate: currentDateRange?.startDate, 
          endDate: currentDateRange?.endDate 
        });
        
        // Проверяем существование контейнера в DOM перед созданием отчета
        const targetContainer = document.getElementById(containerToFill) || 
                        document.querySelector(`[data-container-id="${containerToFill}"]`);
                        
        if (!targetContainer) {
          console.warn(`TrackReport: Целевой контейнер ${containerToFill} не найден в DOM, отмена создания отчета`);
          if (window.showNotification) {
            window.showNotification('warning', 'Контейнер для отчета не найден');
          }
          setContainerToFill(null);
          return;
        }
        
        // Создаем отчет через систему событий
        const createEvent = new CustomEvent('createReport', {
          detail: {
            reportType: selectedReportType,
            container: containerToFill,
            vehicle: currentVehicle,
            startDate: currentDateRange?.startDate,
            endDate: currentDateRange?.endDate,
            timestamp: Date.now(),
            // Добавляем пометку, что это важное событие
            priority: 'high'
          }
        });
        
        // Отправляем событие без задержки
        document.dispatchEvent(createEvent);
        
        // Добавляем визуальную обратную связь для подтверждения выбора отчета
        if (window.showNotification) {
          window.showNotification('success', `Отчет "${selectedReportType}" добавлен в контейнер`);
        }
        
        // Сбрасываем контейнер и направление
        setContainerToFill(null);
        setSplitDirection(null);
        return;
      }
      
      // Проверяем, что направление разделения было установлено
      if (!splitDirection) {
        console.warn('TrackReport: Попытка разделения экрана без указания направления');
        return;
      }
      
      // Создаем событие для создания отчета в контейнере
      const createEvent = new CustomEvent('createReportInContainer', {
        detail: {
          reportType: selectedReportType,
          direction: splitDirection,
          containerId: containerId,
          vehicle: currentVehicle,
          startDate: currentDateRange?.startDate,
          endDate: currentDateRange?.endDate,
          timestamp: Date.now()
        }
      });
      
      document.dispatchEvent(createEvent);
      
      // Сбрасываем направление разделения
      setSplitDirection(null);
      
    } catch (error) {
      console.error('TrackReport: Ошибка при выборе отчета:', error);
      
      // Сбрасываем глобальный флаг в случае ошибки
      window.reportChooserModalOpen = false;
      
      // Показываем ошибку в уведомлении, если доступно
      if (window.showNotification) {
        window.showNotification('error', 'Ошибка при создании отчета: ' + (error.message || 'Неизвестная ошибка'));
      }
      
      // Сбрасываем состояния
      setContainerToFill(null);
      setSplitDirection(null);
    }
  }, [containerToFill, splitDirection, containerId, currentVehicle, currentDateRange]);

  // Обработчик закрытия ReportChooser (копия из TrackMap)
  const handleReportChooserClose = useCallback(() => {
    try {
      console.log('TrackReport: Закрытие ReportChooser');
      
      setShowReportChooser(false);
      
      // Сбрасываем глобальный флаг
      console.log('TrackReport: Сбрасываем глобальный флаг reportChooserModalOpen');
      window.reportChooserModalOpen = false;
      
      // Сбрасываем состояния
      setTimeout(() => {
        setContainerToFill(null);
        setSplitDirection(null);
      }, 100);
      
    } catch (error) {
      console.error('TrackReport: Ошибка при закрытии ReportChooser:', error);
    }
  }, []);

  // Регистрация обработчиков событий разделения экрана (копия из TrackMap)
  useEffect(() => {
    console.log('TrackReport: Регистрация обработчиков событий разделения экрана');
    
    // Регистрируем обработчики событий
    document.addEventListener('splitModeChanged', handleSplitModeChanged);
    document.addEventListener('panelCreated', handlePanelCreated);
    document.addEventListener('requestReportSelector', handleRequestReportSelector);
    
    // Очищаем обработчики при размонтировании
    return () => {
      console.log('TrackReport: Отмена регистрации обработчиков событий разделения экрана');
      document.removeEventListener('splitModeChanged', handleSplitModeChanged);
      document.removeEventListener('panelCreated', handlePanelCreated);
      document.removeEventListener('requestReportSelector', handleRequestReportSelector);
    };
  }, [handleSplitModeChanged, handlePanelCreated, handleRequestReportSelector]);

  // Эффект для регистрации обработчика события завершения разделения контейнера
  useEffect(() => {
    if (!containerId) {
      console.warn('TrackReport: containerId не передан в useSplitScreenManager');
      return;
    }

    // Функция-обработчик события завершения разделения
    const handleSplitComplete = (event) => {
      const { containerId: eventContainerId, container1Id, container2Id, direction } = event.detail;
      
      console.log(`TrackReport: Получено событие завершения разделения`, {
        eventContainerId,
        myContainerId: containerId,
        container1Id,
        container2Id,
        direction
      });
      
      // Проверяем, что это событие связано с нашим контейнером
      // Событие может прийти для родительского контейнера или для нашего контейнера
      const isRelatedToMyContainer = eventContainerId === containerId || 
                                    containerId.startsWith(eventContainerId) ||
                                    eventContainerId.startsWith(containerId) ||
                                    container1Id === containerId ||
                                    container2Id === containerId;
      
      if (isRelatedToMyContainer) {
        console.log(`TrackReport: Событие разделения связано с нашим контейнером ${containerId}`);
        console.log(`TrackReport: Новые контейнеры: ${container1Id}, ${container2Id}, направление: ${direction}`);
        
        // Сбрасываем глобальный флаг перед открытием нового окна
        console.log('TrackReport: Сбрасываем глобальный флаг reportChooserModalOpen');
        window.reportChooserModalOpen = false;
        
        // Небольшая задержка перед открытием ReportChooser
        setTimeout(() => {
          // Открываем окно выбора отчета для второго контейнера
          setContainerToFill(container2Id);
          setShowReportChooser(true);
          
          // Сохраняем направление разделения
          setSplitDirection(direction);
          
          // Устанавливаем флаг глобально, что модальное окно открыто
          window.reportChooserModalOpen = containerId;
          
          console.log(`TrackReport: ReportChooser открыт для контейнера ${container2Id}`);
        }, 100);
      } else {
        console.log(`TrackReport: Событие разделения не связано с нашим контейнером ${containerId}`);
      }
    };
    
    // Добавляем обработчик события
    document.addEventListener('splitContainerComplete', handleSplitComplete);
    
    // Очистка при размонтировании
    return () => {
      document.removeEventListener('splitContainerComplete', handleSplitComplete);
    };
  }, [containerId, setContainerToFill, setShowReportChooser, setSplitDirection]);

  return {
    // Состояния
    expandedMode,
    setExpandedMode,
    showReportChooser,
    setShowReportChooser,
    containerToFill,
    setContainerToFill,
    splitDirection,
    setSplitDirection,
    
    // Функции
    toggleExpandedMode,
    changeSplitMode,
    handleHorizontalSplit,
    handleVerticalSplit,
    handleSingleMode,
    handleGoBack,
    handleReportSelect,
    handleReportChooserClose
  };
}; 